// EventQueue — o motor da política de envio D.5 (JS). Lógica PURA + adaptadores injetáveis
// (transport, storage, timer): é isso que a torna testável em Node e reutilizável (a tese de
// portabilidade). O tracker (browser) injeta os adaptadores de navegador; os testes injetam fakes.
//
//  D.5.1 sinal → envio imediato; analítico → lote.
//  D.5.2 gatilhos: buffer >= bufferSize, janela windowMs, teto maxBatch por payload.
//  D.5.4 persistência/replay: on-fail persiste; no start reenvia (drop > 24h, cap, prioridade do sinal).
//  D.5.5 idempotência: o event_id vem no evento (buildEvent); replay reenvia o MESMO id.
import { classify, DEFAULT_FLUSH, DEFAULT_SIGNAL_EVENTS, REPLAY_MAX_AGE_MS, MAX_BODY_BYTES, BODY_SAFETY_BYTES } from "./contract.js";

/** Os bytes do corpo EXATO que o transporte envia. ESPELHO de sdk-py `_bytes_do_corpo`. */
function bytesDoCorpo(batch) {
  const s = JSON.stringify({ events: batch });
  return typeof TextEncoder !== "undefined" ? new TextEncoder().encode(s).length : unescape(encodeURIComponent(s)).length;
}

/**
 * Quantos eventos do INÍCIO cabem num corpo, por contagem E por bytes. Nunca menos que 1.
 * ESPELHO de sdk-py `_quantos_cabem` — ver `MAX_BODY_BYTES` para a medição que o justifica.
 *
 * Nunca menos que 1 é deliberado: evento que sozinho não cabe sai sozinho. Segurá-lo travaria a
 * fila para sempre atrás de algo que nunca vai caber.
 */
function quantosCabem(eventos, maxItens, maxBytes) {
  let n = 0;
  const teto = Math.min(eventos.length, maxItens);
  for (let i = 0; i < teto; i += 1) {
    if (n && bytesDoCorpo(eventos.slice(0, i + 1)) > maxBytes) break;
    n = i + 1;
  }
  return Math.max(1, n);
}

export class EventQueue {
  /**
   * @param {object} o
   * @param {(batch:object[], opts:{sync?:boolean})=>Promise<boolean>|boolean} o.transport  envia um lote, retorna sucesso
   * @param {{read:()=>object[], write:(list:object[])=>void}|null} [o.storage]  persistência (localStorage no browser)
   * @param {string[]} [o.signalEvents]  conjunto de sinais (classificação)
   * @param {{bufferSize:number,windowMs:number,maxBatch:number}} [o.flush]  gatilhos D.5.2
   * @param {number} [o.maxPending]  teto de eventos persistidos (D.5.4)
   * @param {(fn:()=>void,ms:number)=>any} [o.setTimer] @param {(t:any)=>void} [o.clearTimer]  agendador injetável
   * @param {()=>number} [o.now]
   */
  constructor(o) {
    this.transport = o.transport;
    this.storage = o.storage || null;
    this.signalEvents = o.signalEvents || DEFAULT_SIGNAL_EVENTS;
    this.flush = { ...DEFAULT_FLUSH, ...(o.flush || {}) };
    this.maxPending = o.maxPending || 500;
    this.setTimer = o.setTimer || ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = o.clearTimer || ((t) => clearTimeout(t));
    this.now = o.now || Date.now;
    this.buffer = [];
    this.timer = null;
  }

  /** Enfileira um evento já construído (buildEvent). Sinal vai imediato; analítico entra no lote. */
  enqueue(event) {
    if (classify(event.type, this.signalEvents) === "signal") {
      void this._send([event]);                    // D.5.1: imediato, sem buffer
      return;
    }
    this.buffer.push(event);
    if (this.buffer.length >= this.flush.bufferSize) {
      void this.flushBuffer();                      // D.5.2: gatilho de tamanho
    } else {
      this._scheduleTimer();                        // D.5.2: janela de tempo
    }
  }

  /** Drena o lote analítico. `sync` (saída da página) usa o caminho de beacon do transporte. */
  async flushBuffer(opts) {
    this._clearTimer();
    while (this.buffer.length) {
      // D.5.2: teto de payload — contagem E bytes. O `maxBatch` quase nunca é o que morde.
      const n = quantosCabem(this.buffer, this.flush.maxBatch, MAX_BODY_BYTES - BODY_SAFETY_BYTES);
      const batch = this.buffer.splice(0, n);
      await this._send(batch, opts);
    }
  }

  /** D.5.3: chamado pelos handlers de saída (visibilitychange/pagehide) — drena em modo sync. */
  onExit() {
    void this.flushBuffer({ sync: true });
  }

  async _send(batch, opts = {}) {
    let r = false;
    try {
      r = await this.transport(batch, opts);
    } catch {
      r = false;
    }
    // O transporte pode responder `boolean` (contrato antigo, e o que todo transporte injetado em
    // teste devolve) ou `{ok, permanente}`. Normalizar aqui mantém os dois funcionando.
    const ok = r === true || (r && r.ok === true);
    const permanente = !!(r && r.permanente);
    if (ok) return true;
    if (permanente) {
      // RECUSA QUE NÃO ADIANTA REPETIR — 4xx: credencial inválida, origem não declarada, escopo que
      // a chave não tem, corpo acima do teto do portão.
      //
      // Persistir aqui é o pior dos dois mundos: o lote volta a cada início de sessão, falha de
      // novo, e some 24 h depois sem que ninguém saiba que existiu. Foi assim que um site de
      // cliente ficou QUATRO MESES E MEIO tomando 403 sem um sinal — descoberto em 29/09/2026,
      // e a sessão dele apontou este caminho no nosso SDK depois de ter resolvido no dela.
      //
      // O aviso sai UMA vez por sessão de propósito: um por lote viraria ruído que o dono do site
      // aprende a ignorar, e aí o sinal morre de novo, só que mais alto.
      this._avisarRecusa(r && r.status);
      return false;
    }
    this._persist(batch);                           // D.5.4: falha temporária → persiste p/ replay
    return false;
  }

  /** O aviso da recusa permanente, uma vez por sessão. Não muda o fluxo; só deixa de ser mudo. */
  _avisarRecusa(status) {
    if (this._recusaAvisada) return;
    this._recusaAvisada = true;
    const msg = `[synqi3] evento recusado (HTTP ${status || "4xx"}) e NÃO será reenviado. `
      + "Confira a credencial no header X-SYNQ-TOKEN, se a origem deste site está declarada em "
      + "allowed_origins, e se a chave tem o escopo events:write.";
    try {
      if (typeof console !== "undefined" && console.warn) console.warn(msg);
    } catch {
      /* console indisponível não pode derrubar a ingestão */
    }
  }

  _scheduleTimer() {
    if (this.timer != null) return;
    this.timer = this.setTimer(() => {
      this.timer = null;
      void this.flushBuffer();
    }, this.flush.windowMs);
  }

  _clearTimer() {
    if (this.timer != null) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
  }

  // ── persistência / replay (D.5.4) ──────────────────────────────────────────
  _persist(batch) {
    if (!this.storage) return;
    const pending = this._read();
    const now = this.now();
    for (const ev of batch) pending.push(ev._pts != null ? ev : { ...ev, _pts: now });
    this.storage.write(this._cap(pending));
  }

  _cap(list) {
    if (list.length <= this.maxPending) return list;
    // prioridade de retenção do sinal: descarta o analítico mais antigo primeiro.
    const signals = list.filter((e) => classify(e.type, this.signalEvents) === "signal");
    const analytics = list.filter((e) => classify(e.type, this.signalEvents) !== "signal");
    const room = Math.max(0, this.maxPending - signals.length);
    return signals.concat(analytics.slice(-room));
  }

  _read() {
    try {
      return this.storage.read() || [];
    } catch {
      return [];
    }
  }

  /** Reenvia o pendente da sessão anterior. Chamar uma vez na inicialização. */
  async replay() {
    if (!this.storage) return;
    let pending = this._read();
    const cutoff = this.now() - REPLAY_MAX_AGE_MS;
    pending = pending.filter((e) => (e._pts || 0) >= cutoff);   // D.5.4: descarta > 24h
    this.storage.write([]);                                     // limpa antes de reenviar
    if (!pending.length) return;
    // sinal primeiro (prioridade), depois analítico
    pending.sort((a, b) =>
      (classify(b.type, this.signalEvents) === "signal") - (classify(a.type, this.signalEvents) === "signal"));
    // O REPLAY é o caminho que mais precisa do teto de bytes: ele drena backlog depois de uma
    // queda, que é exatamente quando o lote enche e bate no portão.
    const limpos = pending.map(({ _pts, ...e }) => e);
    const tetoB = MAX_BODY_BYTES - BODY_SAFETY_BYTES;
    for (let i = 0; i < limpos.length;) {
      const n = quantosCabem(limpos.slice(i), this.flush.maxBatch, tetoB);
      await this._send(limpos.slice(i, i + n));                // se falhar, re-persiste
      i += n;
    }
  }
}
